// Booking API client + hooks shared by /book-a-demo and <BookingWidget />.
// Contract: functions/_lib/meetings.ts (GET /api/meetings/availability, POST /api/meetings, POST /api/meetings/cancel, POST /api/meetings/product).
import { useCallback, useEffect, useRef, useState } from 'react';

export type Slot = { start: string; end: string; time?: string };
export type AvailDate = { date: string; weekday: string; label: string; slots: Slot[] };
export type Availability = {
  timezone: string;
  hostTimezone: string;
  slotMinutes: number;
  from: string;
  days: number;
  dates: AvailDate[];
};

export type BookingForm = {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  companySize: string;
  interest: string[];
  industry: string;
  product: string;
  website: string; // honeypot, always empty for humans
};

export const emptyForm: BookingForm = {
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  role: '',
  companySize: '',
  interest: [],
  industry: '',
  product: '',
  website: '',
};

export type Meeting = {
  id: string;
  start: string;
  end: string;
  durationMin: number;
  timezone: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
};

export type BookingResult = {
  meeting: Meeting;
  freeEmail: boolean;
  warnings: string[];
  message: string;
  cancelToken: string;
  icsUrl: string;
  meetingLink: string | null;
  /** True when an Outlook invite was sent from our calendar. */
  invited?: boolean;
};

export type ApiErrorCode =
  | 'VALIDATION'
  | 'INVALID_TIMEZONE'
  | 'SLOT_UNAVAILABLE'
  | 'ALREADY_BOOKED'
  | 'RATE_LIMITED'
  | 'NOT_FOUND'
  | 'SERVER_ERROR'
  | 'NETWORK';

export class ApiError extends Error {
  status: number;
  code: ApiErrorCode;
  fields: Record<string, string>;
  constructor(status: number, code: ApiErrorCode, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

export const CONTACT_EMAIL = 'hello@brandedmark.com';

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK', `We could not reach the booking service. Check your connection, or email ${CONTACT_EMAIL}.`);
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body?.code as ApiErrorCode) || (res.status === 429 ? 'RATE_LIMITED' : 'SERVER_ERROR');
    throw new ApiError(res.status, code, body?.error || `Something went wrong. Please email ${CONTACT_EMAIL}.`, body?.fields || {});
  }
  return body as T;
}

// ---------- time zones and formatting ----------

export const detectTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
  } catch {
    return 'Europe/London';
  }
};

// Few choices on purpose: the detected zone plus the three our visitors use most.
export const COMMON_TZ = ['Europe/London', 'Europe/Amsterdam', 'America/New_York'];
const TZ_LABEL: Record<string, string> = { 'Europe/London': 'UK & Ireland', 'Europe/Amsterdam': 'Central Europe', 'America/New_York': 'US Eastern' };


export const tzOptions = (detected: string) => Array.from(new Set([detected, ...COMMON_TZ]));

export const tzCity = (tz: string) => (tz.split('/').pop() || tz).replace(/_/g, ' ');
export const tzLabel = (tz: string) => TZ_LABEL[tz] || tzCity(tz);

export function tzOffset(tz: string, at: Date = new Date()) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(at);
    return p.find((x) => x.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

const uses12h = (tz: string) => tz.startsWith('America/');

export function fmtTime(iso: string, tz: string) {
  return new Intl.DateTimeFormat(uses12h(tz) ? 'en-US' : 'en-GB', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: uses12h(tz) })
    .format(new Date(iso))
    .replace(/\s?([AP]M)$/i, (_, m) => ` ${m.toLowerCase()}`);
}

export function fmtDay(iso: string, tz: string, long = false) {
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: long ? 'long' : 'short', day: 'numeric', month: long ? 'long' : 'short' }).format(new Date(iso));
}

/** "GMT", "BST", "CET", "EDT": the zone as people say it */
export function tzAbbr(iso: string, tz: string) {
  try {
    return new Intl.DateTimeFormat(uses12h(tz) ? 'en-US' : 'en-GB', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date(iso)).find((p) => p.type === 'timeZoneName')?.value ?? tzCity(tz);
  } catch {
    return tzCity(tz);
  }
}

/** "Tue 21 Oct, 10:30 (London)" */
export const fmtWhen = (iso: string, tz: string) => `${fmtDay(iso, tz)}, ${fmtTime(iso, tz)} (${tzCity(tz)})`;

/** YYYY-MM-DD for "today" in a zone */
export function todayIn(tz: string) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// Plain calendar maths on YYYY-MM-DD strings (UTC noon avoids DST edges).
export const ymdToDate = (ymd: string) => new Date(`${ymd}T12:00:00Z`);
export const dateToYmd = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (ymd: string, n: number) => {
  const d = ymdToDate(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return dateToYmd(d);
};

// ---------- email helpers ----------

const FREE_MAIL = new Set([
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'live.co.uk', 'msn.com',
  'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me', 'protonmail.com',
  'gmx.com', 'gmx.de', 'gmx.net', 'web.de', 'btinternet.com', 'sky.com', 'virginmedia.com', 'mail.com', 'zoho.com', 'hey.com',
]);

export const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
export const emailDomain = (v: string) => v.trim().toLowerCase().split('@')[1] || '';
export const isFreeMail = (v: string) => FREE_MAIL.has(emailDomain(v));

/** acme-tools.co.uk → "Acme Tools" (a guess the visitor can edit) */
export function companyFromEmail(v: string) {
  const d = emailDomain(v);
  if (!d || isFreeMail(v)) return '';
  const name = d.split('.')[0];
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------- source attribution ----------

export function buildSource(src?: string) {
  if (typeof window === 'undefined') return {};
  const q = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  const s = src || q.get('src') || '';
  // The API keeps page/referrer/utm_*; the src tag rides on the page value.
  out.page = window.location.pathname + (s ? `?src=${s}` : '');
  if (document.referrer) out.referrer = document.referrer;
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const v = q.get(k);
    if (v) out[k] = v;
  }
  return out;
}

// ---------- calendar links ----------

const gcalStamp = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');

export function googleCalendarUrl(m: Pick<Meeting, 'start' | 'end'>, meetingLink?: string | null) {
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: 'BrandedMark demo (30 min)',
    dates: `${gcalStamp(m.start)}/${gcalStamp(m.end)}`,
    details: `30 minutes with BrandedMark on Microsoft Teams.${meetingLink ? `\nJoin: ${meetingLink}` : ' We will send the link.'}\nBring one product, and a photo of its serial number or product label if you can.\nQuestions: ${CONTACT_EMAIL}`,
    location: meetingLink || 'Microsoft Teams (link to follow)',
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

// ---------- API calls ----------

export const fetchAvailability = (tz: string, days = 21, signal?: AbortSignal) =>
  request<Availability & { success: true }>(`/api/meetings/availability?from=${todayIn(tz)}&days=${days}&tz=${encodeURIComponent(tz)}`, { signal });

export type BookPayload = {
  start: string;
  timezone: string;
  form: BookingForm;
  source?: string;
};

export function bookMeeting({ start, timezone, form, source }: BookPayload) {
  const message = form.product.trim() ? `One product to look at: ${form.product.trim()}` : undefined;
  return request<BookingResult & { success: true }>('/api/meetings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      start,
      timezone,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      role: form.role || undefined,
      companySize: form.companySize || undefined,
      industry: form.industry || undefined,
      interest: form.interest.length ? form.interest : undefined,
      message,
      source: buildSource(source),
      website: form.website,
    }),
  });
}

export const sendProduct = (token: string, product: string) =>
  request<{ success: true; product: string }>('/api/meetings/product', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, product }),
  });

export const cancelMeeting = (token: string, id?: string) =>
  request<{ success: true; meeting: { id: string; status: string }; alreadyCancelled: boolean }>('/api/meetings/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, id }),
  });

// ---------- hooks ----------

/** Availability for the next `days` days in `tz`. Refetches when tz changes. */
export function useAvailability(tz: string, days = 21) {
  const [data, setData] = useState<Availability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchAvailability(tz, days, ac.signal)
      .then((r) => {
        setData(r);
        setLoading(false);
      })
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message);
        setLoading(false);
      });
    return () => ac.abort();
  }, [tz, days, tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, refresh };
}

const STORE_KEY = 'bm-booking-form-v1';

/** Form state persisted in sessionStorage so a refresh never loses what was typed. */
export function useBookingForm(prefillEmail?: string | null) {
  const [form, setForm] = useState<BookingForm>(() => {
    let saved: Partial<BookingForm> = {};
    try {
      saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
    } catch {
      /* storage blocked */
    }
    const f = { ...emptyForm, ...saved, website: '' };
    if (prefillEmail) f.email = prefillEmail;
    return f;
  });
  useEffect(() => {
    try {
      const { website: _hp, ...rest } = form;
      sessionStorage.setItem(STORE_KEY, JSON.stringify(rest));
    } catch {
      /* storage blocked */
    }
  }, [form]);
  const set = useCallback(<K extends keyof BookingForm>(k: K, v: BookingForm[K]) => setForm((f) => ({ ...f, [k]: v })), []);
  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
  }, []);
  return { form, set, setForm, clear };
}

/** One booking lifecycle: tz + availability + submit + cancel. */
export function useBooking({ source }: { source?: string } = {}) {
  const detected = useRef(detectTz()).current;
  const [tz, setTz] = useState(detected);
  const avail = useAvailability(tz);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BookingResult | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const book = useCallback(
    async (start: string, form: BookingForm) => {
      setSubmitting(true);
      try {
        const r = await bookMeeting({ start, timezone: tz, form, source });
        setResult(r);
        setCancelled(false);
        return r;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'SLOT_UNAVAILABLE') avail.refresh();
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [tz, source, avail.refresh]
  );

  const cancel = useCallback(async () => {
    if (!result) return;
    await cancelMeeting(result.cancelToken, result.meeting.id);
    setCancelled(true);
    avail.refresh();
  }, [result, avail.refresh]);

  const reset = useCallback(() => {
    setResult(null);
    setCancelled(false);
  }, []);

  return { tz, setTz, detected, ...avail, availability: avail.data, submitting, result, cancelled, book, cancel, reset };
}
